import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './Research.scss';

// ── Trefoil path (computed once at module level) ──────────────────────────────
const TREFOIL_PATH = (() => {
  const CX = 100, CY = 100, S = 25, N = 200;
  const pts = [];
  for (let i = 0; i <= N; i++) {
    const t = (i / N) * 2 * Math.PI;
    const x = CX + S * (Math.sin(t) + 2 * Math.sin(2 * t));
    const y = CY - S * (Math.cos(t) - 2 * Math.cos(2 * t));
    pts.push(`${x.toFixed(1)} ${y.toFixed(1)}`);
  }
  return `M ${pts.join(' L ')} Z`;
})();

// ── Node positions ────────────────────────────────────────────────────────────
const NODE_POS = {
  'llm-reasoning':  { x: 100,   y: 62.5,   label: 'Reasoning',  tx: 100,  ty: 48,  anchor: 'middle' },
  'llm-evaluation': { x: 67.5,  y: 118.75, label: 'Evaluation', tx: 48,   ty: 133, anchor: 'end' },
  'llm-alignment':  { x: 132.5, y: 118.75, label: 'Alignment',  tx: 152,  ty: 133, anchor: 'start' },
  'ai-theory':      { x: 100,   y: 100,    label: 'Theory',     tx: 100,  ty: 105, anchor: 'middle' },
};

// Per-node colors matching resume research tags
const NODE_COLORS = {
  'llm-reasoning':  { idle: 'rgba(111, 207, 177, 0.45)', visited: '#4A9E88', active: '#6FCFB1' },
  'llm-evaluation': { idle: 'rgba(0, 133, 161, 0.45)',   visited: '#006880', active: '#0085A1' },
  'llm-alignment':  { idle: 'rgba(255, 174, 110, 0.45)', visited: '#C87D4A', active: '#FFAE6E' },
  'ai-theory':      { idle: 'rgba(217, 164, 65, 0.45)',  visited: '#A67B2E', active: '#D9A441' },
};

// Outward stretch offset per node when active
const ACTIVE_OFFSET = {
  'llm-reasoning':  { dx: 0,  dy: -5 },
  'llm-evaluation': { dx: -3, dy: 3 },
  'llm-alignment':  { dx: 3,  dy: 3 },
  'ai-theory':      { dx: 0,  dy: 0 },
};

// ── Data ──────────────────────────────────────────────────────────────────────
const pillars = [
  {
    id: 'ai-theory',
    name: 'AI Theory',
    question: "What is the mathematical structure beneath a generative model's knowledge — and does classical probability theory even apply to a system whose beliefs are rewritten by every token it emits?",
    sections: [
      {
        label: 'The Commutative Assumption',
        text: "Classical probabilistic modeling rests on a joint distribution that is symmetric and commutative — conditioning on A then B yields the same state as conditioning on B then A. But a generative model's internal [[knowledge]] does not sit still while it is queried: each token it emits becomes part of the context the next token is conditioned on. If order changes the state itself, the commutative assumption may already be broken before we even start reasoning about the model.",
        resonanceMap: {
          'knowledge': 'llm-evaluation',
        },
      },
      {
        label: 'The Non-Commutative Hypothesis',
        text: "Because token concatenation is inherently non-commutative, I investigate whether a language model's admissible parameter space is restricted by consistency relations that hold only across specific token orderings — order-dependent constraints rather than classical, commuting ones. If such structure exists, it would shape not just what a model can represent, but how it must be trained to stay [[aligned]] as its internal state keeps updating.",
        resonanceMap: {
          'aligned': 'llm-alignment',
        },
      },
      {
        label: 'Theory Before the Loop',
        text: 'This question sits underneath the loop of Reasoning, Evaluation, and Alignment: before asking whether a model can [[reason]] efficiently, measure itself honestly, or stay aligned over time, we first need to know what kind of mathematical object its evolving knowledge actually is. Evolving from an initial study at UC Irvine and developed in ongoing discussion with Ralf Herbrich, this work asks whether the same order-dependent structure extends across modalities.',
        resonanceMap: {
          'reason': 'llm-reasoning',
        },
      },
    ],
  },
  {
    id: 'llm-reasoning',
    name: 'LLM Reasoning',
    question: 'How might we algorithmically introduce an artificial constraint during decoding to force LLMs to reason more efficiently?',
    sections: [
      {
        label: 'The Metabolic Problem',
        text: '[[Intuition]] and [[logical reasoning]] serve as the two foundational pillars of human intelligence. Intuition, a highly energy-efficient product of evolution, governs the vast majority of our cognitive processes. Logical reasoning, by contrast, is a metabolically expensive mechanism used to test and correct our intuitions when interacting with complex environments. Ultimately, the goal of reasoning is cognitive efficiency: to compress newly acquired, verified knowledge back into intuitive responses. This continuous cycle of correcting and internalizing is the essence of learning.',
        resonanceMap: {},
      },
      {
        label: 'The Computational Gap',
        text: 'While human intuition evolved under strict [[thermodynamic constraints]] — forcing continuous compression of complex reasoning into rapid, energy-efficient heuristics — the intuition of an LLM is driven by a singular, unconstrained objective: [[next-token prediction]]. Having been trained with massive compute and rewarded for verbosity, LLMs lack the biological imperative to optimize their cognitive load. Their reasoning is unrolled explicitly into the token space, applying the same computational sprawl to trivial queries as to complex ones.',
        resonanceMap: {
          'thermodynamic constraints': 'llm-evaluation',
          'next-token prediction': 'llm-evaluation',
        },
      },
      {
        label: 'The Evolutionary Bridge',
        text: 'They fail to reason efficiently simply because they have never had to [[survive]]. To force this efficiency, we must simulate evolutionary pressure — but we cannot apply pressure to a system we cannot reliably [[measure]]. This exposes our next bottleneck: how we evaluate artificial thought.',
        resonanceMap: {
          'survive': 'llm-alignment',
          'measure': 'llm-evaluation',
        },
      },
    ],
  },
  {
    id: 'llm-evaluation',
    name: 'LLM Evaluation',
    question: "How do we build evaluation benchmarks that are both rigorous enough to expose genuine capability gaps and robust enough to resist Goodhart's Law?",
    sections: [
      {
        label: 'The Measurement Imperative',
        text: '[[Evaluation]] is the foundation of all LLM research. Without reliable measurement, claims about reasoning efficiency and alignment are mere assertions. My evaluation work is not an end in itself — it is the empirical lens through which I validate hypotheses about how LLMs think, fail, and adapt under constraints.',
        resonanceMap: {
          'Evaluation': 'llm-reasoning',
        },
      },
      {
        label: 'The Static Bias',
        text: "In nature, organisms do not evolve by passing static tests; they adapt to dynamic, hostile environments. Current AI evaluation, however, relies on rigid benchmarks that models easily overfit — a pure manifestation of [[Goodhart's Law]], where optimizing for the test degrades actual generalization.",
        resonanceMap: {
          "Goodhart's Law": 'llm-alignment',
        },
      },
      {
        label: 'The Alignment Bridge',
        text: 'To build true resilience, a model must do more than survive dynamic tests; it must [[internalize the underlying rules]] of the environment. This transitions evaluation from a mere testing tool into the ultimate driver of alignment: forcing the model to harmonize its internal representations through continuous environmental trial-and-error.',
        resonanceMap: {
          'internalize the underlying rules': 'llm-alignment',
        },
      },
    ],
  },
  {
    id: 'llm-alignment',
    name: 'LLM Alignment',
    question: 'Can we draw inspiration from the natural efficiency of human interaction to liberate AI alignment from its current computational bottlenecks?',
    sections: [
      {
        label: 'The Dual Order',
        text: 'Human alignment operates across two dimensions of survival. [[Externally]], we adopt shared communication protocols and moral imperatives to ensure social cohesion. [[Internally]], psychological stability depends on aligning our explicit actions with our latent desires and beliefs. We project this dual framework onto AI, demanding both external safety compliance and internal representation fidelity.',
        resonanceMap: {},
      },
      {
        label: 'The Brute-Force Bottleneck',
        text: 'The current alignment paradigm relies heavily on brute-force [[post-training]], demanding vast human effort for preference data and immense compute for reinforcement learning. Humans do not align their values through such exhaustive iteration; we co-evolve through shared symbols and efficient social feedback.',
        resonanceMap: {
          'post-training': 'llm-evaluation',
        },
      },
      {
        label: 'The Multi-Scale Equilibrium',
        text: 'True alignment is never a single-layer fix — [[alignment only exists when it is achieved across all scales simultaneously]]. My research reformulates this as an Information Consistency Problem operating on five distinct scales: latent, agentic, societal, individual, and temporal. By treating alignment as a [[multi-scale convergence]] rather than a post-hoc patch, we close the evolutionary loop: total alignment allows the model to compress heavy, explicit reasoning back into safe, elegant, and low-cost intuition.',
        resonanceMap: {
          'alignment only exists when it is achieved across all scales simultaneously': 'llm-evaluation',
          'multi-scale convergence': 'llm-reasoning',
        },
      },
      {
        label: 'Temporal Alignment — Evolutionary Consistency',
        text: 'A model aligned today may become misaligned tomorrow. Human values are not static — they drift, compound, and sometimes invert across generations. [[Temporal alignment]] asks whether a model\'s value representations remain consistent with a shifting human consensus over time. This is not a fine-tuning problem; it is an [[evolutionary one]]: the model must track and anticipate the trajectory of human values, not merely reflect their current snapshot.',
        resonanceMap: {
          'Temporal alignment': 'llm-evaluation',
          'evolutionary one': 'llm-reasoning',
        },
      },
    ],
  },
];

// ── renderText ────────────────────────────────────────────────────────────────
const renderText = (text, resonanceMap, onResonate) =>
  text.split(/(\[\[.+?\]\])/).map((chunk, i) => {
    if (chunk.startsWith('[[') && chunk.endsWith(']]')) {
      const inner = chunk.slice(2, -2);
      const target = resonanceMap?.[inner];
      if (target) {
        return (
          <span
            key={i}
            className="resonant-keyword"
            onMouseEnter={() => onResonate?.(target)}
            onMouseLeave={() => onResonate?.(null)}
          >
            {inner}
          </span>
        );
      }
      return <strong key={i}>{inner}</strong>;
    }
    return chunk;
  });

// ── Spring config ─────────────────────────────────────────────────────────────
const SPRING = { type: 'spring', stiffness: 350, damping: 26, mass: 0.8 };

// ── TrefoilCanvas ─────────────────────────────────────────────────────────────
const TrefoilCanvas = ({ activeNode, resonatingNode, visitedNodes, collapsed, onNodeClick }) => {
  const [hoveredNode, setHoveredNode] = React.useState(null);
  return (
    <div className="research-canvas-wrap">
      <svg
        className="research-trefoil-svg"
        viewBox="5 35 190 155"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Trefoil paths — scale to 0 at center on collapse */}
        <motion.g
          style={{ transformOrigin: '100px 100px' }}
          animate={collapsed ? { scale: 0, opacity: 0 } : { scale: 1, opacity: 1 }}
          transition={{ duration: 0.75, ease: [0.4, 0, 0.85, 1] }}
        >
          <path className="trefoil-base" d={TREFOIL_PATH} />
          <path className="trefoil-flow" d={TREFOIL_PATH} />
        </motion.g>

        {/* Collapse: shockwave ring */}
        {collapsed && (
          <motion.circle
            cx={100} cy={100}
            fill="none"
            stroke="rgba(200, 125, 74, 0.35)"
            strokeWidth={1}
            initial={{ r: 0, opacity: 1 }}
            animate={{ r: 55, opacity: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut', delay: 0.55 }}
          />
        )}

        {/* Collapse: stable locked circle */}
        {collapsed && (
          <motion.circle
            className="collapse-ring"
            cx={100} cy={100}
            initial={{ r: 0 }}
            animate={{ r: 37.5 }}
            transition={{ type: 'spring', stiffness: 90, damping: 18, delay: 0.6 }}
          />
        )}

        {/* Nodes */}
        {Object.entries(NODE_POS).map(([id, pos]) => {
          const isActive = activeNode === id;
          const isResonating = resonatingNode === id;
          const isVisited = visitedNodes.has(id);

          const isHovered = hoveredNode === id;
          const offset = isActive ? ACTIVE_OFFSET[id] : { dx: 0, dy: 0 };
          const cx = pos.x + offset.dx;
          const cy = pos.y + offset.dy;

          // Glow
          const glowR = isActive ? 16 : isHovered ? 18 : isResonating ? 13 : 0;
          const glowOpacity = isActive ? 0.22 : isHovered ? 0.28 : isResonating ? 0.18 : 0;

          // Node radius
          const nodeR = isActive ? 6.5 : isHovered ? 7.5 : isResonating ? 7 : 5;

          // Label opacity
          const labelOpacity = isActive || isHovered ? 1 : isResonating ? 0.9 : isVisited ? 0.75 : 0.65;

          // Idle class: unvisited, not active, not hovered → subtle pulse
          const isIdle = !isActive && !isHovered && !isVisited && !collapsed;

          return (
            <g
              key={id}
              onMouseEnter={() => setHoveredNode(id)}
              onMouseLeave={() => setHoveredNode(null)}
              style={{ cursor: 'pointer' }}
            >
              {/* Orbit ring — always visible, hint of interactivity */}
              <motion.circle
                cx={pos.x}
                cy={pos.y}
                fill="none"
                stroke="rgba(176, 168, 156, 0.4)"
                strokeWidth={0.7}
                strokeDasharray="2 4"
                animate={{ r: isHovered ? 13 : 10, opacity: isActive ? 0 : 1 }}
                transition={SPRING}
              />

              {/* Glow */}
              <motion.circle
                className="node-glow"
                cx={pos.x}
                cy={pos.y}
                animate={{ r: glowR, opacity: glowOpacity, cx, cy }}
                transition={SPRING}
              />

              {/* Node circle */}
              <motion.circle
                className={`trefoil-node${isActive ? ' active' : ''}${isIdle ? ' idle' : ''}`}
                style={{
                  fill: isActive
                    ? NODE_COLORS[id].active
                    : isVisited
                    ? NODE_COLORS[id].visited
                    : NODE_COLORS[id].idle,
                }}
                animate={{ cx, cy, r: isResonating && !isActive ? [5, 7.5, 5] : nodeR }}
                transition={
                  isResonating && !isActive
                    ? { duration: 0.8, repeat: Infinity, ease: 'easeInOut' }
                    : SPRING
                }
                onClick={() => onNodeClick(id)}
              />

              {/* Hit area */}
              <circle
                cx={pos.x}
                cy={pos.y}
                r={14}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onClick={() => onNodeClick(id)}
              />

              {/* Label */}
              <motion.text
                className="node-label"
                x={pos.tx}
                y={pos.ty}
                textAnchor={pos.anchor}
                animate={{ opacity: labelOpacity }}
                transition={{ duration: 0.25 }}
                style={{ cursor: 'pointer' }}
                onClick={() => onNodeClick(id)}
              >
                {pos.label}
              </motion.text>
            </g>
          );
        })}
      </svg>

      {/* Collapse badge */}
      {collapsed && (
        <motion.div
          className="collapse-badge"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Informational equilibrium
        </motion.div>
      )}
    </div>
  );
};

// ── PillarContent ─────────────────────────────────────────────────────────────
const PillarContent = ({ pillar, onResonate }) => (
  <motion.div
    className="pillar-content"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
  >
    <motion.p
      className="pillar-core-question"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05 }}
    >
      {renderText(pillar.question, {}, null)}
    </motion.p>

    <div className="pillar-sections">
      {pillar.sections.map((sec, i) => (
        <motion.div
          key={i}
          className="pillar-section"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 + i * 0.1 }}
        >
          <p className="pillar-section-label">{sec.label}</p>
          <p className="pillar-section-text">
            {renderText(sec.text, sec.resonanceMap, onResonate)}
          </p>
        </motion.div>
      ))}
    </div>

  </motion.div>
);

// ── Research (main) ───────────────────────────────────────────────────────────
const Research = () => {
  const [activeNode, setActiveNode] = useState(null);
  const [resonatingNode, setResonatingNode] = useState(null);
  const [visitedNodes, setVisitedNodes] = useState(new Set());
  const [collapsed, setCollapsed] = useState(false);
  const [showVision, setShowVision] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const fromResume = location.state?.from === 'resume';
  const targetId = location.hash ? location.hash.slice(1) : null;

  // Handle URL hash navigation
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const pillar = pillars.find(p => p.id === id);
      if (pillar) {
        setActiveNode(id);
        setVisitedNodes(prev => new Set([...prev, id]));
      }
    }
  }, [location]);

  // Trigger collapse when all nodes visited
  useEffect(() => {
    if (visitedNodes.size === pillars.length && !collapsed) {
      const t = setTimeout(() => setCollapsed(true), 500);
      return () => clearTimeout(t);
    }
  }, [visitedNodes, collapsed]);

  // After collapse animation finishes, return all nodes to neutral state
  useEffect(() => {
    if (!collapsed) return;
    const t = setTimeout(() => setActiveNode(null), 1100);
    return () => clearTimeout(t);
  }, [collapsed]);

  const handleNodeClick = useCallback((id) => {
    setActiveNode(prev => (prev === id ? null : id));
    setVisitedNodes(prev => new Set([...prev, id]));
  }, []);

  const activePillar = pillars.find(p => p.id === activeNode) ?? null;

  return (
    <div className="research-page">
      <div className="research-page-header">
        <h2 className="research-heading">Learning and Evolution</h2>
        <button className="research-vision-toggle" onClick={() => setShowVision(v => !v)}>
          Vision <span className={`vision-chevron${showVision ? ' open' : ''}`}>›</span>
        </button>
      </div>

      <AnimatePresence>
        {showVision && (
          <motion.div
            className="research-vision"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <h3>Who am I? Where do I come from? Where am I going?</h3>
            <p>
              I am driven by fundamental scientific questions that remain largely unexplored.
              As we approach the era of Artificial General Intelligence (AGI), my research is guided by these three profound questions.
              I believe that understanding the nature of intelligence is essential to answering them, and all of my research revolves around this central vision.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="research-layout">
        <TrefoilCanvas
          activeNode={activeNode}
          resonatingNode={resonatingNode}
          visitedNodes={visitedNodes}
          collapsed={collapsed}
          onNodeClick={handleNodeClick}
        />

        <div className="research-panel">
          <AnimatePresence mode="wait">
            {activePillar ? (
              <PillarContent
                key={activePillar.id}
                pillar={activePillar}
                onResonate={setResonatingNode}
              />
            ) : (
              <motion.div
                key="prompt"
                className="research-prompt"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="research-intro-text">
                  <h3 className="research-intro-title">The Intelligence Loop</h3>
                  <p>Artificial intelligence today is built on computational sprawl, whereas biological intelligence evolved under strict starvation constraints. My research investigates the missing links between <strong>Reasoning, Evaluation, and Alignment</strong> — treating them not as isolated tasks, but as a unified evolutionary cycle driven by cognitive efficiency.</p>
                  <p className="research-intro-cta">Select a node to explore the loop.</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {fromResume && targetId && activePillar?.id === targetId && (
            <button className="pillar-back" onClick={() => navigate(-1)}>
              ← Back to Resume
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Research;
